/**
 * VideoPlayer Component
 * Handles intro video → idle loop → live avatar session flow
 *
 * IDLE LOOP STRATEGY (matching mobile LoopVideoView):
 * ──────────────────────────────────────────────────────
 * Both <video> elements are ALWAYS opacity:1, position:absolute.
 * We control which one is visible purely through z-index.
 *
 * 1. Active video plays at z-index:2 (visible).
 * 2. Standby video sits at z-index:1 (hidden behind active).
 * 3. ~0.6s before active ends, we start the standby from t=0.
 * 4. We wait for the standby to fire its 'playing' event AND
 *    one additional requestAnimationFrame to guarantee a frame
 *    has been composited by the browser.
 * 5. ONLY THEN do we swap z-index (standby→2, active→1).
 * 6. After the swap, the old active continues playing behind
 *    the new active until it ends naturally, then we rewind it.
 *
 * Because the old active stays at opacity:1/z-index:1 during and
 * after the swap, there is NEVER a moment where neither video
 * covers the container — eliminating ALL flicker.
 */

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAvatarVideos } from '../../config/avatarMapping';
import './VideoPlayer.css';

export type VideoPhase = 'intro' | 'idle' | 'live';

interface VideoPlayerProps {
  companionId: string;
  companionImage?: string;
  companionName?: string;
  isLiveSession?: boolean;
  onStartLiveAvatar?: () => void;
  onStopLiveAvatar?: () => void;
  liveStreamRef?: React.RefObject<HTMLVideoElement>;
  children?: React.ReactNode; // For LiveChatOverlay on mobile
  hasPlayedIntro?: boolean;
  hasMessages?: boolean;
  onIntroEnd?: () => void;
  isMobile?: boolean;
  /** When true on mobile, hides the companion name overlay to avoid overlap with chat */
  isChatVisible?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  companionId,
  companionImage,
  companionName,
  isLiveSession,
  onStartLiveAvatar,
  onStopLiveAvatar,
  liveStreamRef,
  children,
  hasPlayedIntro = false,
  hasMessages = false,
  onIntroEnd,
  isMobile = false,
  isChatVisible = false,
}) => {
  const { t } = useTranslation();
  const videoRefA = useRef<HTMLVideoElement>(null);
  const videoRefB = useRef<HTMLVideoElement>(null);

  // Track which video is currently front (z-index:2).
  const [activeBuffer, setActiveBuffer] = useState<'A' | 'B'>('A');
  const bufferTimeUpdateCleanup = useRef<(() => void) | null>(null);
  // Initialize phase based on whether intro has already played
  const [phase, setPhase] = useState<VideoPhase>(hasPlayedIntro ? 'idle' : 'intro');
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const userHasMuted = useRef(false);
  // Tracks whether the video is currently in idle-loop mode
  const isIdleLooping = useRef(false);
  // Holds the timeupdate listener so we can remove it when leaving idle
  const idleLoopCleanup = useRef<(() => void) | null>(null);
  // Tracks the raw URL we last set on the video element
  const lastSetSrcRef = useRef<string>('');
  // Guard to prevent multiple simultaneous swap attempts
  const isSwappingRef = useRef(false);

  const { intro, idle } = getAvatarVideos(companionId);

  // State for playback control
  const [isPlaying, setIsPlaying] = useState(true);

  // Toggle playback
  const togglePlay = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    const video = videoRefA.current;
    if (video) {
      if (video.paused) {
        video.play().catch(() => {});
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  // Handle phase change interactions
  useEffect(() => {
    if (phase === 'intro' || phase === 'idle') {
      setIsPlaying(true);
    }
  }, [phase]);

  // --- SEAMLESS DOUBLE BUFFER LOOPING ---
  // Mirrors the mobile LoopVideoView strategy exactly:
  //
  // Timeline for each loop cycle:
  //   1. Active video (A) plays at z-index:2, standby (B) is behind at z-index:1.
  //   2. ~1.5s before A ends, we seek B to t=0 and wait for 'seeked'.
  //   3. After B has seeked, we call B.play() and wait for a confirmed video frame
  //      via requestVideoFrameCallback (or double-rAF fallback).
  //   4. ONLY after B has a painted frame do we swap z-index (B→2, A→1).
  //   5. A keeps playing its last frames behind B—it's never hidden or made transparent,
  //      so there's always at least one fully-opaque video covering the container.
  //   6. When A finishes, it naturally stops. It'll be rewound on the next swap.
  //
  // Key invariants:
  //   • Both videos are ALWAYS opacity:1 — the browser never suspends decode.
  //   • Container background is black, but it's never visible because at least
  //     one video always covers it.
  //   • z-index swap is the ONLY thing that changes, and it happens atomically
  //     after a confirmed frame render.
  const attachDoubleBufferLoop = useCallback(() => {
    if (bufferTimeUpdateCleanup.current) {
      bufferTimeUpdateCleanup.current();
      bufferTimeUpdateCleanup.current = null;
    }

    const currentRef = activeBuffer === 'A' ? videoRefA.current : videoRefB.current;
    const nextRef = activeBuffer === 'A' ? videoRefB.current : videoRefA.current;

    if (!currentRef || !nextRef) return;

    // Start the standby video 1.5s before the active video ends.
    // This is deliberately generous—gives Safari plenty of time to
    // seek, decode, and paint the first frame before the swap.
    const SWAP_THRESHOLD_SEC = 1.5;

    const onTimeUpdate = () => {
      if (!isIdleLooping.current || !currentRef.duration) return;
      if (isSwappingRef.current) return;

      if (currentRef.currentTime >= currentRef.duration - SWAP_THRESHOLD_SEC) {
        // Remove the listener immediately so it doesn't fire multiple times
        if (bufferTimeUpdateCleanup.current) {
          bufferTimeUpdateCleanup.current();
          bufferTimeUpdateCleanup.current = null;
        }

        isSwappingRef.current = true;

        // Step 1: Seek the standby to the beginning and wait for the seek to complete.
        // Setting currentTime clears the frame buffer; the 'seeked' event confirms
        // the browser has found the new position and is ready to decode from there.
        nextRef.currentTime = 0;

        const onSeeked = () => {
          nextRef.removeEventListener('seeked', onSeeked);

          // Step 2: Start playing the standby video.
          nextRef
            .play()
            .then(() => {
              // Step 3: Wait for an actual video frame to be rendered.
              // requestVideoFrameCallback is the gold standard—it fires only when
              // a new video frame has been presented to the compositor.
              // Supported in Chrome 83+ and Safari 15.4+.
              const performSwap = () => {
                setActiveBuffer(activeBuffer === 'A' ? 'B' : 'A');
                isSwappingRef.current = false;
              };

              if ('requestVideoFrameCallback' in nextRef) {
                (nextRef as any).requestVideoFrameCallback(() => {
                  // Frame is confirmed painted—safe to swap z-index.
                  performSwap();
                });
              } else {
                // Fallback for older browsers: double requestAnimationFrame.
                // First rAF puts us at the next paint, second rAF confirms
                // that paint was composited to screen.
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    performSwap();
                  });
                });
              }
            })
            .catch(() => {
              // Play failed—unlock the swap guard so the next cycle can retry.
              isSwappingRef.current = false;
            });
        };

        nextRef.addEventListener('seeked', onSeeked, { once: true });
      }
    };

    currentRef.addEventListener('timeupdate', onTimeUpdate);
    bufferTimeUpdateCleanup.current = () =>
      currentRef.removeEventListener('timeupdate', onTimeUpdate);
  }, [activeBuffer]);

  // Re-attach the loop listener whenever the active buffer flips
  useEffect(() => {
    if (phase === 'idle' && isIdleLooping.current) {
      attachDoubleBufferLoop();
    }
    return () => {
      if (bufferTimeUpdateCleanup.current) {
        bufferTimeUpdateCleanup.current();
        bufferTimeUpdateCleanup.current = null;
      }
    };
  }, [phase, attachDoubleBufferLoop]);

  // Handle video ended for intro → idle transition
  const handleVideoEnded = () => {
    if (phase === 'intro') {
      setPhase('idle');
      onIntroEnd?.();
    }
  };

  // Toggle mute state (only applies to intro/live)
  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering other click handlers
    const video = videoRefA.current;
    if (video) {
      const newMutedState = !video.muted;
      video.muted = newMutedState;
      setIsMuted(newMutedState);
      userHasMuted.current = newMutedState;
    }
  };

  // Play appropriate video based on phase
  useEffect(() => {
    if (isLiveSession) {
      isIdleLooping.current = false;
      idleLoopCleanup.current?.();
      idleLoopCleanup.current = null;
      setPhase('live');
      return;
    }

    const video = videoRefA.current;
    if (!video) return;

    // Set poster to companion image to prevent black frames during load
    // Removed because this forces the poster to blink rapidly on every loop crossfade

    const playVideo = async () => {
      const videoA = videoRefA.current;
      const videoB = videoRefB.current;
      if (!videoA || !videoB) return;

      if (phase === 'intro') {
        isIdleLooping.current = false;
        // Use ref-based comparison: video.src is browser-normalized (absolute),
        // but intro may be a relative URL — comparing them always fails.
        if (lastSetSrcRef.current !== intro) {
          setIsVideoReady(false);
          videoA.src = intro;
          lastSetSrcRef.current = intro;
        }
        videoA.loop = false;

        // Reset user mute preference on new intro start related to phase change
        userHasMuted.current = false;

        // Try playing unmuted first for intro
        videoA.muted = false;
        videoA.volume = 1.0;
        setIsMuted(false);

        try {
          const playPromise = videoA.play();
          if (playPromise !== undefined) {
            await playPromise;
            setIsPlaying(true);
          }
        } catch (_err) {
          // Fallback to muted to ensure video runs
          videoA.muted = true;
          setIsMuted(true);
          try {
            await videoA.play();
            setIsPlaying(true);
          } catch (_retryErr) {
            setPhase('idle');
          }
        }
      } else if (phase === 'idle') {
        // Use ref-based comparison (see lastSetSrcRef note above)
        const sourceChanged = lastSetSrcRef.current !== idle;
        if (sourceChanged) {
          // Source is changing — reset ready state and update src
          setIsVideoReady(false);
          videoA.src = idle;
          videoB.src = idle;
          lastSetSrcRef.current = idle;
        }

        // Disable native loop to control our own crossfade buffering
        videoA.loop = false;
        videoB.loop = false;
        videoA.muted = true; // Idle always muted
        videoB.muted = true;

        isIdleLooping.current = true;
        isSwappingRef.current = false;
        setActiveBuffer('A'); // Always start new idle on A

        try {
          // Only play A to start, B will be triggered by timeupdate
          videoA.currentTime = 0;
          await videoA.play();
          setIsPlaying(true);
          attachDoubleBufferLoop();
        } catch (_e) {
          // Idle play failed
        }
      }
    };

    playVideo();
  }, [phase, isLiveSession, intro, idle, attachDoubleBufferLoop]);

  // Global "Unmute Helper": Captures ANY interaction on the page to restore sound
  useEffect(() => {
    const attemptUnmute = () => {
      // Don't auto-unmute if user explicitly muted it
      if (userHasMuted.current) return;

      const video = videoRefA.current;
      if (video && !video.paused && video.muted && phase === 'intro') {
        video.muted = false;
        video.volume = 1.0;
        setIsMuted(false);
      }
    };

    document.addEventListener('click', attemptUnmute);
    document.addEventListener('keydown', attemptUnmute);
    document.addEventListener('touchstart', attemptUnmute);

    return () => {
      document.removeEventListener('click', attemptUnmute);
      document.removeEventListener('keydown', attemptUnmute);
      document.removeEventListener('touchstart', attemptUnmute);
    };
  }, [phase]);

  // Reset phase when live session ends
  useEffect(() => {
    if (!isLiveSession && phase === 'live') {
      setPhase('idle');
    }
  }, [isLiveSession, phase]);

  const handleVideoCanPlay = () => {
    setIsVideoReady(true);
  };

  return (
    <div
      className="video-player-container"
      style={
        companionImage && phase === 'intro'
          ? {
              backgroundImage: `url(${companionImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 10%',
            }
          : { backgroundColor: '#000' }
      }
    >
      {/* Show video during intro/idle phases */}
      {!isLiveSession && (
        <>
          {/* Buffer A — always position:absolute, always opacity:1 */}
          <video
            ref={videoRefA}
            className="video-player"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: activeBuffer === 'A' ? 2 : 1,
              opacity: 1,
              visibility: 'visible',
              pointerEvents: activeBuffer === 'A' ? 'auto' : 'none',
              willChange: 'transform',
              transform: 'translateZ(0) scale(1.02)',
            }}
            onEnded={handleVideoEnded}
            onCanPlay={handleVideoCanPlay}
            playsInline
            preload="auto"
          >
            <track kind="captions" src="" label="English" />
          </video>

          {/* Buffer B — always position:absolute, always opacity:1 */}
          <video
            ref={videoRefB}
            className="video-player"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: activeBuffer === 'B' ? 2 : 1,
              opacity: 1,
              visibility: 'visible',
              pointerEvents: activeBuffer === 'B' ? 'auto' : 'none',
              willChange: 'transform',
              transform: 'translateZ(0) scale(1.02)',
            }}
            onEnded={handleVideoEnded}
            playsInline
            preload="auto"
          >
            <track kind="captions" src="" label="English" />
          </video>

          {/* Playback Controls Overlay - Only visible during intro phase */}
          {phase === 'intro' && (
            <div className="video-controls-overlay">
              {/* Play/Pause Button */}
              <button
                type="button"
                className="video-control-btn"
                onClick={togglePlay}
                title={isPlaying ? 'Pause' : 'Play'}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              {/* Mute/Unmute Button */}
              <button
                type="button"
                className="video-control-btn"
                onClick={toggleMute}
                title={isMuted ? 'Unmute' : 'Mute'}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                    <path d="M9 9v6a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                    <line x1="12" y1="19" x2="12" y2="23"></line>
                    <line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                    <line x1="12" y1="19" x2="12" y2="23"></line>
                    <line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Show live stream during live session */}
      {isLiveSession && liveStreamRef && (
        <video ref={liveStreamRef} className="video-player live-stream" autoPlay playsInline>
          <track kind="captions" src="" label="English" />
        </video>
      )}

      {/* Fallback image if video not ready */}
      {!isVideoReady && !isLiveSession && companionImage && (
        <img
          src={companionImage}
          alt={companionName || 'Avatar'}
          className="video-player-fallback"
        />
      )}

      {/* Avatar name overlay - hidden when chat is open on mobile to avoid messy overlap */}
      {!isLiveSession && (
        <div className={`video-player-overlay${isMobile && isChatVisible ? ' chat-open' : ''}`}>
          <div className="video-player-info">
            {companionImage && (
              <div className="avatar-thumbnail">
                <img src={companionImage} alt="" />
              </div>
            )}
            <div className="avatar-name-label">
              <span className="avatar-name">{companionName}</span>
            </div>
          </div>
        </div>
      )}

      {/* Start/Stop Live Avatar Button — hidden on mobile via CSS (overlay handles it) */}
      <div className={`video-player-controls${isMobile ? ' hide-on-mobile' : ''}`}>
        {!isLiveSession ? (
          <button type="button" className="start-live-btn" onClick={onStartLiveAvatar}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            {t('chat.startLiveSession', 'Start Live Session')}
          </button>
        ) : (
          <button type="button" className="stop-live-btn" onClick={onStopLiveAvatar}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
            {t('chat.endLiveSession', 'End Session')}
          </button>
        )}
      </div>

      {/* Mobile chat overlay slot */}
      {children}
    </div>
  );
};

export default VideoPlayer;
